import React, { useState, useEffect, useCallback } from 'react';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAuth } from '../../../../../context/AuthUserContext';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { setInviteId, claimVenue } from '../../../../../lib/apis/claim';
import { localeFiles } from '../../../../../data/constants';
import { toLocationStr, getErrorMsg } from '../../../../../lib/utils';
import PageDefault from '../../../../../components/templates/PageDefault';
import ServiceProviderDetails from '../../../../../components/templates/ServiceProviderDetails';
import AccountLayer from '../../../../../components/organisms/AccountLayer';
import Button from '../../../../../components/atoms/Button';
import RequestForm from '../../../../../components/organisms/RequestForm';
import styles from '../../../../../styles/pages/ServiceProvider.module.scss';
import { prisma } from '../../../../../db';
import { HTTP_URL } from '../../../../../env.server';
import { Review } from '@prisma/client';
import { serializePrismaObject } from '../../../../../lib/utils';

interface ServiceProviderProps {
  serviceProvider: any;
  origin: string;
  referer?: string;
}

const ServiceProvider = (props: ServiceProviderProps) => {
  const itemType = 'service';
  const { t } = useTranslation(['common', itemType]);
  const { user } = useAuth();
  const router = useRouter();
  const [layer, setLayer] = useState() as any;
  const [fullLayer, setFullLayer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [error, setError] = useState() as any;
  const { query } = router;
  const { invite_code, autoClaim } = query as any;
  const { serviceProvider, referer } = props;
  const { id, name, description, location } = serviceProvider;
  const { address } = location || {};

  /*
   * Claim
   */
  const claim = useCallback(
    (itemID) => {
      if (claiming || !user || !!claimed) {
        return;
      }
      console.log('claimServieProvider', itemID);
      // setLoading(true);
      // setClaiming(true);
      // claimVenue(itemID)
      //   .then((response: any) => {
      //     if (response.status !== 200) {
      //       const errorMsg = getErrorMsg(response.data);
      //       setError(t(`common:form-error`) + ` (${errorMsg})`);
      //     } else if (user.onboarding_complete) {
      //       router.push(`/my-businesses`);
      //     }
      //   })
      //   .catch((error) => {
      //     const errorMsg = getErrorMsg(error);
      //     setError(t(`common:form-error`) + ` (${errorMsg})`);
      //   })
      //   .finally(() => {
      //     setLoading(false);
      //   });
    },
    [user, claimed, claiming]
  );

  /**
   * stores the invite ID to keep invitation context
   */
  useEffect(() => {
    if (!!invite_code && id) {
      setInviteId(id, invite_code);
    }
    // if (id && !window.location.href.includes('autoClaim=1')) {
    //   setLoading(false);
    // }
  }, [invite_code, id]);

  /**
   * triggers autoClaim
   */
  useEffect(() => {
    if (!!user && !!id && window.location.href.includes('autoClaim=1')) {
      console.log('autoClaim', id);
      claim(id);
    }
  }, [user, claim, id]);

  /**
   * Check if user owns venue
   * Remove invite code if it still exists but venue is claimed
   */
  useEffect(() => {
    if (!!id && !claimed && !!user) {
      if (
        user.service_providers.find(
          (admin) => admin.service_provider_id === id
        ) ||
        user.email.includes('@venuescout.org')
      ) {
        setClaimed(true);
        console.log(
          'remove invite parameter',
          user.service_providers.find(
            (admin) => admin.service_provider_id === id
          )
        );
        router.replace(router.asPath.split('?')[0], undefined, {
          shallow: true,
        });
      }
    }
  }, [id, user, claimed, router]);

  /*
   * Proceed to claim or login
   */
  const onToastClick = () => {
    if (!!user) {
      claim(id);
    } else {
      setLayer({
        closeBtn: false,
        bg: false,
        children: (
          <AccountLayer
            currView={'login'}
            toggle={() => setLayer({ children: null })}
            redirect={`${props.origin}/service-provider/${props.serviceProvider.country}/${props.serviceProvider.city}/${props.serviceProvider.slug}?invite_code=${invite_code}&autoClaim=1`}
          />
        ),
      });
    }
  };

  /**
   * Sets the claim venue toast
   */
  const toastClasses = clsx(
    { [styles.error]: !!error },
    { [styles.loading]: loading }
  );

  /**
   * Sets a button with anchor link
   */
  const anchorBtn =
    loading || (!!user && claimed)
      ? undefined
      : {
          label: t(`${itemType}:cta-proposal`),
          el: 'contact',
          icon: 'proposal',
          onClick: () =>
            setLayer({
              bg: true,
              large: true,
              children: (
                <RequestForm
                  data={serviceProvider}
                  type="service"
                  onSubmit={() => setLayer({})}
                />
              ),
            }),
        };

  return (
    <>
      <Head>
        <title>
          {name} | {t('common:app-name')}
        </title>
        <meta name="description" content={description || ''} />
      </Head>

      <PageDefault
        className={styles.serviceProviderPage}
        layer={layer}
        fullLayer={fullLayer}
        isLoading={loading || (autoClaim && !error)}
        anchor={anchorBtn}
      >
        <ServiceProviderDetails
          data={serviceProvider}
          claimed={!!user && claimed}
          setLayer={setLayer}
          referer={referer}
        />
      </PageDefault>
    </>
  );
};

export default ServiceProvider;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { params: { slug, country, city } = {}, locale } = context;

  try {
    const serviceProvider = await prisma.serviceProvider.findUnique({
      where: {
        country_city_slug: {
          slug: String(slug),
          city: String(city),
          country: String(country),
        },
      },
      include: {
        sales_contacts: {
          include: {
            image: true,
          },
        },
        management_contacts: {
          include: {
            image: true,
          },
        },
        cities: {
          where: {
            approved_service_provider_id: {
              not: null,
            },
          },
        },
        categories: {
          include: {
            images: true,
          },
        },
        files: {
          select: {
            id: true,
            name: true,
            type: true,
            size: true,
            url: true,
            category: true,
            description: true,
          },
        },
        contact_details: true,
        location: true,
        venues: true,
        reviews: true,
        images: true,
        subscriptions: true,
      },
    });

    if (!serviceProvider || serviceProvider.deleted_at) {
      return {
        notFound: true,
      };
    }

    const reviews = serviceProvider.reviews.map((item: Review) => ({
      ...item,
      createdAt: JSON.parse(JSON.stringify(item.createdAt)),
    }));

    return {
      props: {
        origin: HTTP_URL,
        referer: context.req.headers.referer || '/',
        serviceProvider: { ...serializePrismaObject(serviceProvider), reviews },
        ...(await serverSideTranslations(locale!, localeFiles)),
      },
    };
  } catch (e) {
    console.error(e);
  }
}
